import {
  ConditionAction,
  ConfigValues,
  DataInput,
  DataInputType,
  DatasetType,
  DynamicDataInputConfig,
  FileSelectionMode,
  FilterMode,
  InputSource,
  LinkedDataInputConfig,
  StaticDataInputConfig,
  EmptyDataInputConfig,
  StepType,
} from '@/types/automationFields.ts';
import {
  AutomationStep,
  ControlBlockingMode,
  EntityDataInputConfig,
  EntityModeType,
  ValidateStep,
} from '@/types/automationSteps.ts';
import { getAllStepChildren, indexStepsById, isStepWithChildren } from '@/utils/stepUtils.ts';

export enum StepConnectionKind {
  DataInput = 'data_input',
  StaticDataset = 'static_dataset',
  LinkedDataset = 'linked_dataset',
  EmptyDataset = 'empty_dataset',
  WaitForStep = 'wait_for_step',
  ValidationBlocksStep = 'validation_blocks_step',
  ValidationBlocksLaterStep = 'validation_blocks_later_step',
}

export type StepConnection = {
  id: string;
  kind: StepConnectionKind;
  stepId: string | null;
  stepName: string | null;
  stepType: StepType | null;
  outputId: string | null;
  datasetId: string | null;
  datasetType: DatasetType | null;
  displayName: string;
  detail: string;
};

export type StepConnections = {
  uses: StepConnection[];
  referencedBy: StepConnection[];
};

type RawStepConnection = {
  sourceStepId: string;
  kind: StepConnectionKind;
  connectedStepId: string | null;
  outputId: string | null;
  datasetId: string | null;
  datasetType: DatasetType | null;
  displayName: string | null;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isDataInput = (value: unknown): value is DataInput => {
  if (!isObject(value)) return false;
  if (!('data_input_type' in value) || !('config' in value)) return false;
  if (!isObject(value.config)) return false;
  return Object.values(DataInputType).includes(value.data_input_type as DataInputType);
};

const isConfigValues = (value: unknown): value is ConfigValues => {
  if (!isObject(value)) return false;
  return Array.isArray(value.fields);
};

function acceptsDataInput(source: InputSource | undefined): boolean {
  return source === InputSource.FromDataset || source === InputSource.Both;
}

const stepConnectionKindsUsedBySourceStep = new Set<StepConnectionKind>([
  StepConnectionKind.DataInput,
  StepConnectionKind.WaitForStep,
]);

const stepConnectionKindsAffectingTargetStep = new Set<StepConnectionKind>([
  StepConnectionKind.ValidationBlocksStep,
  StepConnectionKind.ValidationBlocksLaterStep,
]);

function makeConnectionId(raw: RawStepConnection): string {
  return [
    raw.sourceStepId,
    raw.kind,
    raw.connectedStepId ?? '',
    raw.outputId ?? '',
    raw.datasetId ?? '',
    raw.datasetType ?? '',
    raw.displayName ?? '',
  ].join(':');
}

function getDataInputDisplayName(dataInput: DataInput): string | null {
  return dataInput.name || dataInput.last_known_display_name || null;
}

function dataInputToRawConnection(
  sourceStepId: string,
  dataInput: DataInput,
): RawStepConnection | null {
  const displayName = getDataInputDisplayName(dataInput);

  if (dataInput.data_input_type === DataInputType.Dynamic) {
    const { source_step_id, output_id } = dataInput.config as DynamicDataInputConfig;
    if (!source_step_id) return null;
    return {
      sourceStepId,
      kind: StepConnectionKind.DataInput,
      connectedStepId: source_step_id,
      outputId: output_id,
      datasetId: null,
      datasetType: null,
      displayName,
    };
  }

  if (dataInput.data_input_type === DataInputType.Static) {
    const { static_dataset_id } = dataInput.config as StaticDataInputConfig;
    if (!static_dataset_id) return null;
    return {
      sourceStepId,
      kind: StepConnectionKind.StaticDataset,
      connectedStepId: null,
      outputId: null,
      datasetId: static_dataset_id,
      datasetType: null,
      displayName,
    };
  }

  if (dataInput.data_input_type === DataInputType.Linked) {
    const { linked_dataset_id } = dataInput.config as LinkedDataInputConfig;
    if (!linked_dataset_id) return null;
    return {
      sourceStepId,
      kind: StepConnectionKind.LinkedDataset,
      connectedStepId: null,
      outputId: null,
      datasetId: linked_dataset_id,
      datasetType: null,
      displayName,
    };
  }

  if (dataInput.data_input_type === DataInputType.Empty) {
    const { dataset_type } = dataInput.config as EmptyDataInputConfig;
    return {
      sourceStepId,
      kind: StepConnectionKind.EmptyDataset,
      connectedStepId: null,
      outputId: null,
      datasetId: null,
      datasetType: dataset_type,
      displayName,
    };
  }

  return null;
}

function collectDataInputConnectionsFromValue(stepId: string, value: unknown): RawStepConnection[] {
  const connections: RawStepConnection[] = [];
  const seen = new Set<unknown>();

  const visit = (node: unknown) => {
    if (!isObject(node) || seen.has(node)) return;
    seen.add(node);

    if (isDataInput(node)) {
      const connection = dataInputToRawConnection(stepId, node);
      if (connection) connections.push(connection);
      return;
    }

    if (isConfigValues(node)) {
      node.fields.forEach((field) => {
        if (!field.is_hidden && !field.is_disabled) {
          visit(field.value);
        }
      });
      return;
    }

    Object.entries(node).forEach(([key, value]) => {
      if (key === 'children') return;
      visit(value);
    });
  };

  visit(value);
  return connections;
}

function collectDataInputConnectionsFromInputs(
  stepId: string,
  dataInputs: Array<DataInput | undefined>,
): RawStepConnection[] {
  return dataInputs
    .filter((dataInput): dataInput is DataInput => !!dataInput)
    .map((dataInput) => dataInputToRawConnection(stepId, dataInput))
    .filter((connection): connection is RawStepConnection => !!connection);
}

function collectActiveDataInputConnections(step: AutomationStep): RawStepConnection[] {
  switch (step.step_type) {
    case StepType.Trigger:
    case StepType.Dispatch:
      return collectDataInputConnectionsFromValue(
        step.step_id,
        step.integration_config.config_values,
      );
    case StepType.ExtractData: {
      const connections = collectDataInputConnectionsFromInputs(step.step_id, step.data_inputs);
      if (acceptsDataInput(step.instructions_source)) {
        connections.push(
          ...collectDataInputConnectionsFromInputs(step.step_id, step.instructions_data_inputs),
        );
      }
      if (acceptsDataInput(step.extract_fields_source)) {
        connections.push(
          ...collectDataInputConnectionsFromInputs(step.step_id, [step.extract_fields_data_input]),
        );
      }
      if (step.extract_entities && step.entity_mode === EntityModeType.DataInput) {
        connections.push(
          ...collectDataInputConnectionsFromInputs(step.step_id, [
            (step.entity_config as EntityDataInputConfig).data_input,
          ]),
        );
      }
      return connections;
    }
    case StepType.TransformData: {
      const connections = collectDataInputConnectionsFromInputs(step.step_id, step.data_inputs);
      if (acceptsDataInput(step.transformation_logic_source)) {
        connections.push(
          ...collectDataInputConnectionsFromInputs(
            step.step_id,
            step.transformation_logic_data_inputs,
          ),
        );
      }
      return connections;
    }
    case StepType.SubAutomation:
      return collectDataInputConnectionsFromInputs(
        step.step_id,
        step.trigger_input_mappings.map((mapping) => mapping.data_input),
      );
    case StepType.LocalSubflowCall:
      return collectDataInputConnectionsFromInputs(
        step.step_id,
        step.input_bindings.map((binding) => binding.data_input),
      );
    case StepType.Iterate:
      return collectDataInputConnectionsFromInputs(step.step_id, [
        step.data_input,
        ...step.data_outputs.map((output) => output.child_data_output),
      ]);
    case StepType.Repeat: {
      const repeatDataInputs = step.repeat_data_inputs.flatMap((input) => [
        input.data_input,
        input.child_data_output,
      ]);
      const connections = collectDataInputConnectionsFromInputs(step.step_id, [
        ...repeatDataInputs,
        ...step.condition_data_inputs,
      ]);
      if (acceptsDataInput(step.condition_logic_source)) {
        connections.push(
          ...collectDataInputConnectionsFromInputs(step.step_id, step.condition_logic_data_inputs),
        );
      }
      return connections;
    }
    case StepType.Retry:
      return collectDataInputConnectionsFromInputs(
        step.step_id,
        step.data_outputs.map((output) => output.child_data_output),
      );
    case StepType.FailureHandler:
      return collectDataInputConnectionsFromInputs(step.step_id, [
        ...(step.protected_input_bindings || []).map((binding) => binding.data_input),
        ...(step.handler_input_bindings || []).map((binding) => binding.data_input),
        ...step.protected_success_outputs.map((output) => output.child_data_output),
        ...step.recovery_outputs.map((output) => output.child_data_output),
        ...(step.recovery_continuation_outputs ?? []).map((output) => output.child_data_output),
      ]);
    case StepType.Conditional:
      return step.conditions.flatMap((condition) => {
        const conditionInputs: DataInput[] = [...condition.condition_data_inputs];
        if (condition.action === ConditionAction.ReturnDataset) {
          conditionInputs.push(
            ...condition.data_outputs.flatMap((output) => [
              output.data_input,
              output.child_data_output,
            ]),
          );
        }
        const connections = collectDataInputConnectionsFromInputs(step.step_id, conditionInputs);
        if (acceptsDataInput(condition.condition_logic_source)) {
          connections.push(
            ...collectDataInputConnectionsFromInputs(
              step.step_id,
              condition.condition_logic_data_inputs,
            ),
          );
        }
        return connections;
      });
    case StepType.CreateDirectory:
      return collectDataInputConnectionsFromInputs(step.step_id, step.data_inputs);
    case StepType.GetFileFromDirectory: {
      const connections = collectDataInputConnectionsFromInputs(step.step_id, [step.data_input]);
      if (
        (step.selection_mode ?? FileSelectionMode.NaturalLanguage) ===
          FileSelectionMode.NaturalLanguage &&
        acceptsDataInput(step.file_to_get_source)
      ) {
        connections.push(
          ...collectDataInputConnectionsFromInputs(step.step_id, [step.file_to_get_data_input]),
        );
      }
      if (
        (step.selection_mode ?? FileSelectionMode.NaturalLanguage) !==
          FileSelectionMode.NaturalLanguage &&
        acceptsDataInput(step.selection_value_source)
      ) {
        connections.push(
          ...collectDataInputConnectionsFromInputs(step.step_id, [step.selection_value_data_input]),
        );
      }
      return connections;
    }
    case StepType.Filter: {
      const connections = collectDataInputConnectionsFromInputs(step.step_id, [step.data_input]);
      if (
        step.filter_mode === FilterMode.Condition &&
        acceptsDataInput(step.filter_condition_source)
      ) {
        connections.push(
          ...collectDataInputConnectionsFromInputs(
            step.step_id,
            step.filter_condition_data_inputs,
          ),
        );
      }
      if (step.filter_mode === FilterMode.Selection && acceptsDataInput(step.selection_source)) {
        connections.push(
          ...collectDataInputConnectionsFromInputs(step.step_id, step.selection_data_inputs),
        );
      }
      return connections;
    }
    case StepType.Validate: {
      const connections = collectDataInputConnectionsFromInputs(step.step_id, step.data_inputs);
      if (acceptsDataInput(step.validation_logic_source)) {
        connections.push(
          ...collectDataInputConnectionsFromInputs(step.step_id, step.validation_logic_data_inputs),
        );
      }
      return connections;
    }
    case StepType.HumanDecisionGate:
      return collectDataInputConnectionsFromInputs(step.step_id, step.data_inputs);
    case StepType.RunMetadata:
      return [];
  }
}

function collectStepAndDescendantIds(step: AutomationStep): Set<string> {
  const ids = new Set<string>([step.step_id]);
  if (isStepWithChildren(step)) {
    getAllStepChildren(step).forEach((child) => {
      collectStepAndDescendantIds(child).forEach((childId) => ids.add(childId));
    });
  }
  return ids;
}

function collectDirectStepConnectionsInScope(scopeSteps: AutomationStep[]): RawStepConnection[] {
  const connections: RawStepConnection[] = [];

  scopeSteps.forEach((step, index) => {
    connections.push(...collectActiveDataInputConnections(step));

    step.wait_for_step_ids.forEach((waitForStepId) => {
      connections.push({
        sourceStepId: step.step_id,
        kind: StepConnectionKind.WaitForStep,
        connectedStepId: waitForStepId,
        outputId: null,
        datasetId: null,
        datasetType: null,
        displayName: null,
      });
    });

    if (step.step_type === StepType.Validate) {
      const validateStep = step as ValidateStep;
      if (validateStep.blocking_mode === ControlBlockingMode.SelectedSteps) {
        validateStep.blocking_step_ids.forEach((blockedStepId) => {
          connections.push({
            sourceStepId: validateStep.step_id,
            kind: StepConnectionKind.ValidationBlocksStep,
            connectedStepId: blockedStepId,
            outputId: null,
            datasetId: null,
            datasetType: null,
            displayName: null,
          });
        });
      }

      if (validateStep.blocking_mode === ControlBlockingMode.AllLaterStepsInScope) {
        scopeSteps.slice(index + 1).forEach((blockedStep) => {
          collectStepAndDescendantIds(blockedStep).forEach((blockedStepId) => {
            connections.push({
              sourceStepId: validateStep.step_id,
              kind: StepConnectionKind.ValidationBlocksLaterStep,
              connectedStepId: blockedStepId,
              outputId: null,
              datasetId: null,
              datasetType: null,
              displayName: null,
            });
          });
        });
      }
    }
  });

  scopeSteps.forEach((step) => {
    if (!isStepWithChildren(step)) return;
    if (step.step_type === StepType.Conditional) {
      step.conditions.forEach((condition) => {
        connections.push(...collectDirectStepConnectionsInScope(condition.children));
      });
    } else {
      connections.push(...collectDirectStepConnectionsInScope(step.children));
    }
  });

  return connections;
}

function getConnectionDetail(kind: StepConnectionKind, outputId: string | null): string {
  switch (kind) {
    case StepConnectionKind.DataInput:
      return outputId ? `Uses output "${outputId}"` : 'Uses step output';
    case StepConnectionKind.StaticDataset:
      return 'Uses static data source';
    case StepConnectionKind.LinkedDataset:
      return 'Uses linked data source';
    case StepConnectionKind.EmptyDataset:
      return 'Uses empty dataset placeholder';
    case StepConnectionKind.WaitForStep:
      return 'Waits for this step';
    case StepConnectionKind.ValidationBlocksStep:
      return 'Validation gate references this step';
    case StepConnectionKind.ValidationBlocksLaterStep:
      return 'Validation gate affects this later step';
  }
}

function toStepConnection(
  raw: RawStepConnection,
  connectedStepId: string | null,
  stepsById: Record<string, AutomationStep>,
): StepConnection {
  const connectedStep = connectedStepId ? stepsById[connectedStepId] : null;
  const displayName =
    raw.displayName ||
    connectedStep?.name ||
    raw.datasetId ||
    (raw.datasetType ? `Empty ${raw.datasetType}` : 'Unknown connection');

  return {
    id: makeConnectionId(raw),
    kind: raw.kind,
    stepId: connectedStep?.step_id ?? null,
    stepName: connectedStep?.name ?? null,
    stepType: connectedStep?.step_type ?? null,
    outputId: raw.outputId,
    datasetId: raw.datasetId,
    datasetType: raw.datasetType,
    displayName,
    detail: getConnectionDetail(raw.kind, raw.outputId),
  };
}

function uniqueConnections(connections: StepConnection[]): StepConnection[] {
  const seen = new Set<string>();
  return connections.filter((connection) => {
    if (seen.has(connection.id)) return false;
    seen.add(connection.id);
    return true;
  });
}

export function collectStepConnections(
  steps: AutomationStep[] | undefined,
  stepId: string,
): StepConnections {
  if (!steps?.length) return { uses: [], referencedBy: [] };

  const stepsById = indexStepsById(steps);
  const rawConnections = collectDirectStepConnectionsInScope(steps);

  const uses = rawConnections
    .filter((connection) => connection.sourceStepId === stepId)
    .filter((connection) => {
      if (connection.connectedStepId === stepId) return false;
      return !stepConnectionKindsAffectingTargetStep.has(connection.kind);
    })
    .map((connection) => toStepConnection(connection, connection.connectedStepId, stepsById));

  const referencedBy = rawConnections
    .filter((connection) => connection.connectedStepId === stepId)
    .filter((connection) => connection.sourceStepId !== stepId)
    .filter((connection) => {
      return (
        stepConnectionKindsUsedBySourceStep.has(connection.kind) ||
        stepConnectionKindsAffectingTargetStep.has(connection.kind)
      );
    })
    .map((connection) => toStepConnection(connection, connection.sourceStepId, stepsById));

  const affectedByValidation = rawConnections
    .filter((connection) => connection.sourceStepId === stepId)
    .filter((connection) => stepConnectionKindsAffectingTargetStep.has(connection.kind))
    .filter((connection) => connection.connectedStepId !== stepId)
    .map((connection) => toStepConnection(connection, connection.connectedStepId, stepsById));

  return {
    uses: uniqueConnections(uses),
    referencedBy: uniqueConnections([...referencedBy, ...affectedByValidation]),
  };
}
